import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Button, MenuItem, Select, CircularProgress, Typography, useMediaQuery,
} from '@mui/material';
import { Folder } from 'lucide-react';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

export type FolderOption = { id: number; path: string };

// One folder-picker option: the folder's own name on the left, its full path
// from Root on the right (muted). Shared by this popup and upload-document.tsx.
// Root itself passes no path.
export const FolderOptionLabel: React.FC<{ path?: string }> = ({ path }) => {
    const name = path ? path.split(' / ').pop() : 'Root';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
            <Folder size={14} color={path ? 'var(--color-primary)' : 'var(--color-text-muted)'} style={{ flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{name}</Typography>
            {path && (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', ml: 'auto', pl: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Root / {path}
                </Typography>
            )}
        </Box>
    );
};

export interface MoveDocumentProps {
    open: boolean;
    onClose: () => void;
    onMoved: () => void;
    entityId: number | null;
    documentSlug: string | null;
    documentTitle: string;
    currentFolderId: number | null;
    folders: FolderOption[];
    // False for system-filed documents (invoices, report shares), which have
    // to stay inside their own system folder.
    allowRoot?: boolean;
}

const ROOT_VALUE = '__root__';

const MoveDocument: React.FC<MoveDocumentProps> = ({ open, onClose, onMoved, entityId, documentSlug, documentTitle, currentFolderId, folders, allowRoot = true }) => {
    const { showError, showSuccess } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [target, setTarget] = useState<string>(ROOT_VALUE);
    const [moveDoc, setMoveDoc] = useApi();

    useEffect(() => {
        if (open) setTarget(currentFolderId ? String(currentFolderId) : ROOT_VALUE);
    }, [open, currentFolderId]);

    const sortedFolders = useMemo(() => [...folders].sort((a, b) => a.path.localeCompare(b.path)), [folders]);

    const handleMove = () => {
        if (!documentSlug || !entityId) return;
        setMoveDoc({
            ...buildApiRequestConfig(routes.documents.update, [{ param: 'slug', value: documentSlug }]),
            body: { entityId, folderId: target === ROOT_VALUE ? null : Number(target) },
        });
    };

    useEffect(() => {
        const { data, isLoading, error } = moveDoc;
        if (!isLoading && data) { showSuccess('Document moved'); onMoved(); onClose(); }
        if (error) showError(typeof error === 'string' ? error : 'Failed to move document');
    }, [moveDoc.data, moveDoc.isLoading, moveDoc.error]);

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>Move "{documentTitle}"</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Box sx={{ pt: 1 }}>
                    <Select
                        fullWidth size="small"
                        value={target}
                        onChange={e => setTarget(e.target.value)}
                        sx={{ borderRadius: 2 }}
                    >
                        {allowRoot && (
                            <MenuItem value={ROOT_VALUE}>
                                <FolderOptionLabel />
                            </MenuItem>
                        )}
                        {sortedFolders.map(f => (
                            <MenuItem key={f.id} value={String(f.id)}>
                                <FolderOptionLabel path={f.path} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 110, '&:hover': { background: 'var(--color-container-background)' } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleMove}
                    variant="contained"
                    disabled={moveDoc.isLoading}
                    startIcon={moveDoc.isLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
                    sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 110, '&:hover': { background: 'var(--color-primary-hover)' } }}
                >
                    Move
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default MoveDocument;
